import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X, Clock, ChevronRight, Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ExternalLink } from '@/components/ui/external-link';
import { DealMapping } from '../types/api';
import { apiClient, StripeEnvironment } from '../services/api';
import { hubspotDealUrl, stripeSubscriptionUrl } from '../utils/externalUrls';
import { formatCurrency } from '../utils/formatCurrency';
import { isSyncBlocked, syncBlockedDetail } from '../utils/syncEligibility';
import { describeSyncStatus } from '../utils/syncStatus';
import { StripeIcon } from './icons/LogoIcons';
import LinkExistingDialog from './LinkExistingDialog';
import SyncConfirmationDialog from './SyncConfirmationDialog';


interface DealListItemProps {
  deal: DealMapping;
  onClick: () => void;
  hubspotPortalId?: string;
  stripeAccountId?: string;
  stripeEnvironment?: StripeEnvironment;
}


const DealListItem: React.FC<DealListItemProps> = ({ deal, onClick, hubspotPortalId, stripeAccountId, stripeEnvironment }) => {
  const queryClient = useQueryClient();
  // A historically-linked deal carries a real subscription, just one Finrite
  // didn't create — display it like a synced deal.
  const isLinked = deal.sync_status === 'linked' && deal.stripe_subscription_id;
  // A pure one-time deal is billed as a standalone Stripe invoice — the deal
  // row's own sync_status/stripe_subscription_id can't tell us that (there's
  // no subscription). one_off_invoice_status is authoritative and independent.
  const isInvoiceSynced = deal.one_off_invoice_status === 'synced' && !!deal.one_off_invoice_id;
  const isInvoiceLinked = deal.one_off_invoice_status === 'linked' && !!deal.one_off_invoice_id;
  const isInvoiceBilled = isInvoiceSynced || isInvoiceLinked;
  const isSynced = (deal.sync_status === 'synced' && deal.stripe_subscription_id) || isLinked || isInvoiceBilled;
  const hasFailed = deal.sync_status === 'failed';
  // The server could not build this row at all. Handled explicitly, because
  // every branch here matches an exact status and an unrecognised one falls
  // through to the "not synced" tail — which would report a deal we know is
  // broken as a perfectly ordinary unsynced one, in the one view where the
  // reason is never shown.
  const isUnavailable = deal.sync_status === 'unavailable';
  const unavailableReason = deal.sync_status_message || deal.error_message || undefined;
  const isNotSynced = deal.sync_status === 'not_synced';
  // Auto-matched but unconfirmed — the user must pick/confirm via the dialog.
  const isAwaiting = deal.sync_status === 'awaiting_selection';
  // The list shows deals whose stage cannot sync (an open deal with a quote),
  // so every Sync affordance below has to be disabled for them — the API would
  // refuse the call, and an enabled button that does nothing is worse than a
  // greyed-out one that says why.
  const syncBlocked = isSyncBlocked(deal);
  const syncBlockedReason = syncBlocked ? syncBlockedDetail(deal) : undefined;
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const syncMutation = useMutation({
    mutationFn: () => apiClient.syncDeal(deal.hubspot_deal_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });

  const requestSync = () => {
    setShowSyncConfirmation(true);
  };

  const confirmSync = () => {
    setShowSyncConfirmation(false);
    // Re-checked here, not just where the button rendered: the list polls, so
    // a deal can become unsyncable — merged in HubSpot, or unreadable — while
    // this dialog sits open, and the dialog is mounted outside the branch that
    // hides the action. Confirming then would sync a deal the row itself no
    // longer offers to sync.
    if (syncBlocked) return;
    syncMutation.mutate();
  };

  // Derive display state: mutation states take priority over server data
  const justSynced = syncMutation.isSuccess && syncMutation.data?.status === 'created';
  const justExisted = syncMutation.isSuccess && syncMutation.data?.status === 'already_exists';
  const showSynced = isSynced || justSynced || justExisted;
  // An unavailable row outranks a mutation error. The error is from an attempt
  // earlier in this session; "unavailable" is the server saying, now, that it
  // cannot read this deal — and because the error branch is checked first, the
  // row would show "Failed" beside a Retry that is disabled for an unavailable
  // deal anyway: a dead end, with the reason hidden behind it.
  //
  // showSynced is deliberately left alone. A sync that just completed is real
  // news about what the user did, no dead-end control comes with it, and the
  // next poll corrects it once the row renders again.
  const showError = syncMutation.isError && !isUnavailable;
  const displaySubscriptionId =
    deal.stripe_subscription_id || syncMutation.data?.subscription_id;

  // Extract error message from mutation error
  const syncErrorMessage = syncMutation.error
    ? (syncMutation.error as any)?.response?.data?.detail ||
      (syncMutation.error as any)?.response?.data?.error ||
      (syncMutation.error as Error).message ||
      'Sync failed'
    : null;

  return (
    <Card
      onClick={onClick}
      className="hover:shadow-md transition-shadow cursor-pointer mb-3"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          {/* Left side - HubSpot */}
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground">
                {deal.dealname || 'Unnamed Deal'}
              </div>
              <div className="text-xs text-muted-foreground">
                {hubspotPortalId ? (
                  <ExternalLink href={hubspotDealUrl(hubspotPortalId, deal.hubspot_deal_id)} className="text-xs">
                    {deal.hubspot_deal_id}
                  </ExternalLink>
                ) : (
                  deal.hubspot_deal_id
                )}
              </div>
              {deal.amount && (
                <div className="text-xs text-muted-foreground mt-1">
                  {formatCurrency(deal.amount, deal.deal_currency_code)}
                </div>
              )}
            </div>
          </div>

          {/* Sync status indicator */}
          <div className="px-4 flex flex-col items-center gap-1">
            {syncMutation.isPending ? (
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            ) : showError ? (
              <X className="w-6 h-6 text-destructive" />
            ) : showSynced ? (
              <>
                <Check className="w-6 h-6 text-green-500" />
                {/* Distinguish a user-confirmed link from a Finrite sync. */}
                <span className="text-[10px] font-medium uppercase tracking-wide text-green-600">
                  {isLinked || isInvoiceLinked ? 'Linked' : 'Synced'}
                </span>
              </>
            ) : hasFailed || isUnavailable ? (
              // Both are bad states. Without isUnavailable here the row wore a
              // waiting clock on one side and a destructive label on the
              // other, reading as two different deals.
              <X className="w-6 h-6 text-destructive" />
            ) : (
              <Clock className="w-6 h-6 text-muted-foreground" />
            )}
          </div>

          {/* Right side - Stripe */}
          <div className={`flex items-center space-x-4 flex-1 justify-end ${
            isNotSynced && !syncMutation.isPending && !showSynced && !showError ? 'opacity-50' : ''
          }`}>
            <div className="flex-1 text-right">
              {syncMutation.isPending ? (
                <div className="text-sm text-muted-foreground">Syncing...</div>
              ) : showSynced ? (
                <>
                  <div className="text-sm font-semibold text-foreground">
                    {displaySubscriptionId
                      ? (deal.billing_model === 'metered'
                          ? 'Metered Billing'
                          : deal.billing_model === 'metered_subscription'
                            ? 'Metered + Recurring'
                            : 'Subscription')
                      : isInvoiceBilled
                        ? 'One-off invoice'
                        : ''}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {displaySubscriptionId ? (
                      stripeEnvironment ? (
                        <ExternalLink
                          href={stripeSubscriptionUrl(stripeEnvironment, displaySubscriptionId, stripeAccountId)}
                          className="text-xs"
                        >
                          {displaySubscriptionId}
                        </ExternalLink>
                      ) : (
                        displaySubscriptionId
                      )
                    ) : isInvoiceBilled && deal.one_off_invoice_id ? (
                      deal.one_off_invoice_id
                    ) : null}
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    {deal.stripe_subscription_status && (
                      <Badge variant="outline">
                        {deal.stripe_subscription_status}
                      </Badge>
                    )}
                    {deal.billing_model && deal.billing_model !== 'subscription' && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        metered
                      </Badge>
                    )}
                    {/*
                      Subtle warnings pill — only shown when sync succeeded
                      AND warnings exist. Hidden on failed deals because
                      the destructive error alert below already dominates
                      that signal.
                    */}
                    {deal.warnings && deal.warnings.length > 0 && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-700/50 gap-0.5"
                        title={
                          deal.warnings.length === 1
                            ? deal.warnings[0].title ?? deal.warnings[0].code
                            : `${deal.warnings.length} warnings — click for details`
                        }
                      >
                        <AlertTriangle className="h-2.5 w-2.5" />
                        {deal.warnings.length}
                      </Badge>
                    )}
                  </div>
                </>
              ) : showError ? (
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-destructive">Failed</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      syncMutation.reset();
                      syncMutation.mutate();
                    }}
                    disabled={syncBlocked}
                    title={syncBlockedReason}
                  >
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Retry
                  </Button>
                </div>
              ) : isUnavailable ? (
                // No Retry: retrying syncs the deal, and a deal whose data
                // could not be read is exactly what must not be billed from.
                // The row says what happened and stops there.
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm text-destructive">
                    {describeSyncStatus(deal.sync_status).label}
                  </span>
                  {/* Visible text, not a title: a title is hover-only, so on
                      touch, by keyboard, or through a screen reader the reason
                      simply wasn't there — and the reason is the whole point
                      of showing this row rather than hiding it. */}
                  {unavailableReason && (
                    <span className="text-xs text-destructive">{unavailableReason}</span>
                  )}
                </div>
              ) : hasFailed ? (
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-destructive">Sync failed</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      syncMutation.mutate();
                    }}
                    disabled={syncMutation.isPending || syncBlocked}
                    title={syncBlockedReason}
                  >
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Retry
                  </Button>
                </div>
              ) : isNotSynced ? (
                <div className="flex items-center justify-end gap-2">
                  {/* Any unsynced deal can be linked to an existing Stripe
                      subscription, not just historical ones. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLinkDialog(true);
                    }}
                  >
                    Link Existing…
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestSync();
                    }}
                    disabled={syncMutation.isPending || syncBlocked}
                    title={syncBlockedReason}
                  >
                    Sync
                  </Button>
                </div>
              ) : isAwaiting ? (
                <div className="flex items-center justify-end gap-2">
                  <span className="text-sm text-amber-700">Needs review</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLinkDialog(true);
                    }}
                  >
                    Link Existing…
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestSync();
                    }}
                    disabled={syncMutation.isPending || syncBlocked}
                    title={syncBlockedReason}
                  >
                    Sync
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Not synced</div>
              )}
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              showSynced ? 'bg-white' : 'bg-muted'
            }`}>
              <StripeIcon className="w-8 h-8" />
            </div>
          </div>

          {/* Chevron */}
          <div className="ml-4 text-muted-foreground">
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>

        {/* Error message - from server data or mutation error */}
        {hasFailed && deal.error_message && !showError && (
          <Alert variant="destructive" className="mt-2 py-2">
            <AlertDescription className="text-xs">{deal.error_message}</AlertDescription>
          </Alert>
        )}
        {showError && syncErrorMessage && (
          <Alert variant="destructive" className="mt-2 py-2">
            <AlertDescription className="text-xs">{syncErrorMessage}</AlertDescription>
          </Alert>
        )}
      </CardContent>

      <SyncConfirmationDialog
        open={showSyncConfirmation}
        dealId={deal.hubspot_deal_id}
        onOpenChange={setShowSyncConfirmation}
        onConfirm={confirmSync}
        isSyncing={syncMutation.isPending}
      />

      {showLinkDialog && (
        <div onClick={(e) => e.stopPropagation()}>
          <LinkExistingDialog
            deal={deal}
            open={showLinkDialog}
            onOpenChange={setShowLinkDialog}
          />
        </div>
      )}
    </Card>
  );
};

export default DealListItem;
